import { useCallback, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { History, Loader } from 'lucide-react';

type Anchor = { scrollHeight: number; scrollTop: number };

/**
 * Keeps the message that was at the top in view when older messages are prepended: `capture`
 * records the container's height before loading, and the next transcript change restores
 * scrollTop by the height the prepend added. `restored()` is true only for that one commit, so
 * scroll-to-bottom effects can skip it. `release` drops an anchor whose load prepended nothing,
 * so it cannot shift a later, unrelated render.
 */
export function usePrependScroll(containerRef: { current: HTMLElement | null }, messages: unknown) {
  const anchorRef = useRef<Anchor | null>(null);
  const restoredRef = useRef(false);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const container = containerRef.current;
    restoredRef.current = false;
    if (!anchor || !container) return;

    anchorRef.current = null;
    restoredRef.current = true;
    container.scrollTop = anchor.scrollTop + (container.scrollHeight - anchor.scrollHeight);
  }, [messages, containerRef]);

  const capture = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    anchorRef.current = { scrollHeight: container.scrollHeight, scrollTop: container.scrollTop };
  }, [containerRef]);

  const release = useCallback(() => {
    anchorRef.current = null;
  }, []);

  const restored = useCallback(() => {
    return restoredRef.current;
  }, []);

  return { capture, release, restored };
}

export function LoadEarlierMessages({
  chat,
  onBeforeLoad,
  onNothingLoaded,
}: {
  chat: { hasEarlierParts?: boolean; loadingEarlier?: boolean; loadEarlier?: () => Promise<boolean>; status?: string };
  onBeforeLoad: () => void;
  /** The load resolved without prepending (failure, empty page, early exit). */
  onNothingLoaded: () => void;
}) {
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');

  if (!chat.hasEarlierParts) {
    return null;
  }

  const isStreaming = chat.status === 'submitted' || chat.status === 'streaming';

  async function load() {
    onBeforeLoad();
    const prepended = await chat.loadEarlier?.();
    if (!prepended) {
      onNothingLoaded();
    }
  }

  return (
    <div className="flex justify-center mb-6">
      <button
        type="button"
        onClick={() => void load()}
        disabled={chat.loadingEarlier || isStreaming}
        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center gap-2 text-black transition-colors disabled:opacity-50"
      >
        {chat.loadingEarlier ? (
          <>
            <Loader className="h-4 w-4 animate-spin" />
            {tc('loading')}
          </>
        ) : (
          <>
            <History className="h-4 w-4" />
            {t('controls.load-earlier-messages')}
          </>
        )}
      </button>
    </div>
  );
}
